import { createClient } from "@supabase/supabase-js";
// PHI overlay (plan §28): PHI columns from RDS, the rest from Supabase.
// Deferred requires inside that module keep the import graph acyclic.
import { wrapSupabaseClientForPhiOverlay } from "../utils/rdsPhiOverlay";
// Storage overlay (plan §29): buckets from RDS once proven against the live
// object; uploads / removes mirrored. Same deferred-require rule.
import { wrapSupabaseClientForStorageOverlay } from "../utils/rdsStorageOverlay";
import { supabaseUrl, supabaseKey, supabaseServiceKey } from "./env.js";
import { setAuthContext, clearAuthContext } from "../utils/userEventTracker";
import { wrapSupabaseClientForReadOnlyRole, markBlockedStorageWrite } from "../utils/readOnlySupabaseProxy";

/**
 * Custom Web Lock for Supabase auth.
 *
 * Chrome throws immediately when navigator.locks can't be acquired (e.g. two
 * windows/tabs of the same app — main page + ExternalRecordingPage — competing
 * for the same lock). The default Supabase behaviour uses { ifAvailable: true }
 * which rejects on contention instead of waiting.
 *
 * This replacement drops { ifAvailable: true } so Chrome queues callers and
 * grants the lock as soon as the previous holder releases it, eliminating the
 * "Acquiring an exclusive Navigator LockManager lock … immediately failed" error.
 * Falls back to running fn() directly in environments without Web Locks support.
 */
const authLock = (name, acquireTimeout, fn) => {
  if (typeof navigator === "undefined" || !navigator.locks) {
    return fn();
  }
  return navigator.locks.request(name, fn);
};

// Singleton instances to prevent multiple GoTrueClient instances
let _supabasePublic = null;
let _supabasePublicAdmin = null;
let _supabaseAdmin = null; // singleton for getSupabaseAdmin()
let _supabaseAdminPublic = null; // singleton for getSupabaseAdminPublic()
let _supabase = null;
let _supabaseImpersonation = null;
export let supabaseSchema = "public";
let supabaseRole = null;
// Current signed-in user's role, as resolved during schema/role load. Populated
// once `getSupabase()`/`loadSupabaseSchema()` has run. Use to gate role-specific
// behavior (e.g. skip clinician-only maintenance jobs for read-only patients).
export function getSupabaseRole() {
  return supabaseRole;
}
let schemaLoaded = false; // Track if schema has been loaded
let schemaLoadingPromise = null; // Prevents concurrent loadSupabaseSchema() calls
let authSessionCallCount = 0; // Track auth.getSession() calls for debugging DB spam

// Export for debugging - access getAuthSessionCallCount() in browser console
export function getAuthSessionCallCount() {
  return authSessionCallCount;
}

// Initialize public clients only once
function getSupabasePublic() {
  if (!_supabasePublic) {
    // Validate that config has been loaded
    if (!supabaseUrl || !supabaseKey) {
      throw new Error(
        `Supabase configuration not loaded. supabaseUrl: ${!!supabaseUrl}, supabaseKey: ${!!supabaseKey}. ` +
          `Make sure loadConfig() is called before using Supabase clients.`,
      );
    }
    _supabasePublic = createClient(supabaseUrl, supabaseKey, {
      auth: { lock: authLock },
      db: { schema: "public" },
    });
  }
  return _supabasePublic;
}

function getSupabasePublicAdmin() {
  if (!_supabasePublicAdmin) {
    // Validate that config has been loaded
    if (!supabaseUrl || !supabaseServiceKey) {
      throw new Error(
        `Supabase configuration not loaded. supabaseUrl: ${!!supabaseUrl}, supabaseServiceKey: ${!!supabaseServiceKey}. ` +
          `Make sure loadConfig() is called before using Supabase clients.`,
      );
    }
    _supabasePublicAdmin = createClient(supabaseUrl, supabaseServiceKey, {
      // Service-role client: its auth MUST stay isolated from the logged-in
      // user. Without persistSession:false + a distinct storageKey it shares
      // the default `sb-<ref>-auth-token` storage with the user client, loads
      // that session, and sends the user's JWT (role: authenticated) as the
      // Authorization bearer — so it stops acting as service_role and Storage
      // RLS rejects writes ("new row violates row-level security policy").
      // Same isolation pattern the impersonation client already uses.
      auth: {
        lock: authLock,
        persistSession: false,
        autoRefreshToken: false,
        storageKey: "sb-public-admin",
      },
      db: { schema: "public" },
    });
  }
  return _supabasePublicAdmin;
}

// Export the getter functions instead of the instances
export {
  getSupabasePublic as supabasePublic,
  getSupabasePublicAdmin as supabasePublicAdmin,
};

// Backward-compatible exports for code that imports `supabase` directly
// These are proxy objects that delegate to the singleton to prevent multiple GoTrueClient instances
// Use getSupabase() for schema-aware client, these for direct public client access

// Create a proxy that delegates all property access to the singleton client
const createClientProxy = (getClientFn) => {
  const getWrappedClient = () =>
    wrapSupabaseClientForStorageOverlay(
      wrapSupabaseClientForPhiOverlay(
        wrapSupabaseClientForReadOnlyRole(getClientFn(), supabaseRole, {
          blockWritesWhileRoleUnknown: !schemaLoaded,
        }),
        "public",
      ),
      "public",
    );

  return new Proxy(
    {},
    {
      get(target, prop) {
        const client = getWrappedClient();
        return Reflect.get(client, prop, client);
      },
      set(target, prop, value) {
        const client = getWrappedClient();
        return Reflect.set(client, prop, value, client);
      },
      apply(target, thisArg, args) {
        const client = getWrappedClient();
        return client(...args);
      },
    },
  );
};

export const supabase = createClientProxy(getSupabasePublic);
export const supabaseA = createClientProxy(getSupabasePublic);
export const supabaseAdmin = createClientProxy(getSupabaseAdmin);

function _getOrCreateImpersonationClient() {
  // Lazy-built when isImpersonating() flips true. Cleared on exit.
  // Imported lazily to avoid circular dependency at module load.
  // eslint-disable-next-line global-require
  const { isImpersonating, getImpersonationState } = require("../utils/impersonation");
  if (!isImpersonating()) {
    _supabaseImpersonation = null;
    return null;
  }
  if (_supabaseImpersonation) return _supabaseImpersonation;
  const state = getImpersonationState();
  if (!state) return null;
  if (!supabaseUrl || !supabaseKey) {
    throw new Error("Supabase configuration not loaded for impersonation client");
  }
  const schema = state.target?.active_schema || "public";
  // Mirror the target's active schema into the module-level `supabaseSchema`
  // so any code that reads it (path-generation helpers, etc.) sees the
  // impersonated user's tenant rather than admin's stale value. The full
  // page reload on exit re-runs loadSupabaseSchema() and restores admin's.
  supabaseSchema = schema;
  _supabaseImpersonation = createClient(supabaseUrl, supabaseKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      storageKey: "sb-imp",
    },
    db: { schema },
  });
  // supabase-js setSession() rejects empty refresh_token. Pass the access_token
  // as a placeholder — autoRefreshToken: false prevents any actual refresh attempt,
  // so the placeholder never travels over the wire. When the access_token expires
  // (8h), the next call returns 401 and the banner exits.
  _supabaseImpersonation.auth.setSession({
    access_token: state.token,
    refresh_token: state.token,
  });
  return _supabaseImpersonation;
}

// Get the current Supabase client, loading the schema if necessary.
// Uses a shared promise so concurrent callers wait on the same load instead
// of each firing their own auth.getUser() — which causes Web Lock contention.
export async function getSupabase() {
  // eslint-disable-next-line global-require
  const { isImpersonating } = require("../utils/impersonation");
  if (isImpersonating()) {
    const c = _getOrCreateImpersonationClient();
    if (c) {
      return wrapSupabaseClientForStorageOverlay(
        wrapSupabaseClientForPhiOverlay(wrapSupabaseClientForReadOnlyRole(_wrapForViewAsBlock(c), supabaseRole), supabaseSchema),
        supabaseSchema,
      );
    }
  }
  if (!schemaLoaded) {
    if (!schemaLoadingPromise) {
      schemaLoadingPromise = loadSupabaseSchema().finally(() => {
        schemaLoadingPromise = null;
      });
    }
    await schemaLoadingPromise;
  }
  return wrapSupabaseClientForStorageOverlay(
    wrapSupabaseClientForPhiOverlay(wrapSupabaseClientForReadOnlyRole(_wrapForViewAsBlock(_supabase), supabaseRole), supabaseSchema),
    supabaseSchema,
  );
}

// Get an admin client for the current schema.
// Returns a singleton — creating a new client on every call was causing multiple
// GoTrueClient instances to compete for the same Web Lock, producing 5s timeouts.
export function getSupabaseAdmin() {
  if (!supabaseUrl || !supabaseServiceKey) {
    throw new Error(
      `Supabase configuration not loaded. supabaseUrl: ${!!supabaseUrl}, supabaseServiceKey: ${!!supabaseServiceKey}. ` +
        `Make sure loadConfig() is called before using Supabase clients.`,
    );
  }
  if (!_supabaseAdmin) {
    _supabaseAdmin = createClient(supabaseUrl, supabaseServiceKey, {
      // Isolate service-role auth from the user session (see getSupabasePublicAdmin) —
      // otherwise this client authorizes as the logged-in user and RLS blocks
      // its storage/db writes (e.g. clinic-doc `.json` autosave → 400).
      auth: {
        lock: authLock,
        persistSession: false,
        autoRefreshToken: false,
        storageKey: "sb-admin",
      },
      db: { schema: supabaseSchema },
    });
  }
  // The service-role client does the document flows' storage writes
  // (Yjs backups, version history, clinic-doc autosave), so the storage
  // overlay wraps it too; the PHI overlay does not (its table reads are
  // admin-only and stay on Supabase).
  return wrapSupabaseClientForStorageOverlay(
    wrapSupabaseClientForReadOnlyRole(_supabaseAdmin, supabaseRole), supabaseSchema,
  );
}

// Get an admin client that always uses the public schema.
// Returns a singleton for the same reason as getSupabaseAdmin().
export function getSupabaseAdminPublic() {
  if (!supabaseUrl || !supabaseServiceKey) {
    throw new Error(
      `Supabase configuration not loaded. supabaseUrl: ${!!supabaseUrl}, supabaseServiceKey: ${!!supabaseServiceKey}. ` +
        `Make sure loadConfig() is called before using Supabase clients.`,
    );
  }
  if (!_supabaseAdminPublic) {
    _supabaseAdminPublic = createClient(supabaseUrl, supabaseServiceKey, {
      // Isolate service-role auth from the user session (see getSupabasePublicAdmin).
      auth: {
        lock: authLock,
        persistSession: false,
        autoRefreshToken: false,
        storageKey: "sb-admin-public",
      },
      db: { schema: "public" },
    });
  }
  return wrapSupabaseClientForStorageOverlay(
    wrapSupabaseClientForReadOnlyRole(_supabaseAdminPublic, supabaseRole), "public",
  );
}

// Load the user-specific schema and reinitialize the supabase client
export async function loadSupabaseSchema() {
  console.log("[supabaseClient] loadSupabaseSchema() starting...");
  const publicClient = getSupabasePublic();

  // Race against a 8s timeout — auth.getUser() can hang for 30+ seconds on
  // a Supabase 522 (Cloudflare gateway timeout), which freezes the whole app.
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(
      () =>
        reject(
          new Error(
            "auth.getUser() timed out after 8s — Supabase may be unreachable (522)",
          ),
        ),
      8000,
    ),
  );

  // Use getSession() instead of getUser(): both run __loadSession() (refresh if needed), but getUser()
  // also GETs /user — an extra Auth API call on every cold load. Schema resolution only needs user.id
  // from the session; ProtectedRoute also relies on getSession() for access checks.
  authSessionCallCount++;
  console.log(
    `[CLIENT] Auth session call #${authSessionCallCount} (getSession())`,
  );
  let user, authError, accessToken;
  try {
    const { data: sessionData, error: sessionErr } = await Promise.race([
      publicClient.auth.getSession(),
      timeoutPromise,
    ]);
    authError = sessionErr;
    user = sessionData?.session?.user ?? null;
    accessToken = sessionData?.session?.access_token ?? null;
  } catch (timeoutErr) {
    console.warn(
      "[Supabase] " +
        (timeoutErr.message || timeoutErr) +
        ". Falling back to public schema.",
    );
    supabaseSchema = "public";
    supabaseRole = null;
    _supabase = publicClient;
    schemaLoaded = true;
    return;
  }

  if (authError || !user) {
    if (authError) {
      const is522 =
        authError.message?.includes("522") ||
        authError.message?.includes("Failed to fetch") ||
        authError.status === 522;
      const is429 =
        authError.status === 429 ||
        String(authError.message || "")
          .toLowerCase()
          .includes("rate limit");
      if (is522) {
        console.warn(
          "[Supabase] Auth check failed with a 522 Cloudflare timeout — Supabase is momentarily unreachable. Falling back to public schema until connection is restored.",
        );
      } else if (is429) {
        explainSupabaseError(authError, "loadSupabaseSchema");
        console.warn(
          "[Supabase] Auth returned HTTP 429 (rate limit) during session/refresh. The Supabase client may clear the local session for this error, which can send the user to /403 even though their account is fine. " +
            "Common causes: many tabs/devices, shared IP, or project Auth rate limits. Ask the user to wait a few minutes, use a single tab, then sign in again; check Supabase Dashboard → Project → Rate limits and status.supabase.com.",
        );
      } else {
        console.warn(
          "[Supabase] Auth check returned an error, falling back to public schema:",
          authError.message || authError,
        );
      }
    } else {
      console.log(
        "[Supabase] No active session — using public schema (user not logged in).",
      );
    }
    supabaseSchema = "public"; // Keep default schema
    supabaseRole = null;
    _supabase = publicClient; // Use public client
    schemaLoaded = true;
    return;
  }

  const { data, error } = await publicClient
    .from("users")
    .select("schema, role")
    .eq("id", user.id)
    .single();

  if (error) {
    console.log("Error fetching schema, defaulting to public:", error.message);
    supabaseSchema = "public";
    supabaseRole = null;
    _supabase = publicClient;
    schemaLoaded = true;
    return;
  }

  supabaseRole = data?.role || null;

  if (!data?.schema) {
    // Using default public schema
    supabaseSchema = "public";
    _supabase = publicClient;
    schemaLoaded = true;
    return;
  }

  // Support comma-separated schemas — the first entry is the active selection.
  // The "Switch Schema" button reorders this list to put the selected schema
  // first, so honoring the first entry is what makes schema switching work for
  // every role (clinical roles included).
  supabaseSchema = data.schema.includes(",")
    ? data.schema.split(",")[0].trim()
    : data.schema.trim();

  // Validate that config has been loaded
  if (!supabaseUrl || !supabaseKey) {
    throw new Error(
      `Supabase configuration not loaded. supabaseUrl: ${!!supabaseUrl}, supabaseKey: ${!!supabaseKey}. ` +
        `Make sure loadConfig() is called before using Supabase clients.`,
    );
  }

  _supabase = createClient(supabaseUrl, supabaseKey, {
    auth: { lock: authLock },
    db: { schema: supabaseSchema },
  });
  console.log("[supabaseClient] Schema loaded successfully:", supabaseSchema);
  // Load the user-event tracker only when this is NOT a view-as session.
  // During view-as, we don't want admin's browsing to count as the target's events.
  // eslint-disable-next-line global-require
  const isImp = (() => {
    try {
      return require("../utils/impersonation").isImpersonating();
    } catch (_) { return false; }
  })();
  // Arm the user-event tracker for every schema, public included: the backend
  // keeps only presence pings for X-Schema: public (views.user_events_batch),
  // which is what makes public-only accounts show as online in Messages.
  if (accessToken && !isImp && supabaseSchema) {
    setAuthContext({ token: accessToken, schema: supabaseSchema });
  }
  // Keep the user-event tracker's cached bearer token in sync with Supabase's
  // auto-refresh — otherwise heartbeats start returning 401 once the initial
  // access_token expires (~1h after sign-in). Skip during view-as.
  _supabase.auth.onAuthStateChange((event, session) => {
    if (isImp) return;
    if (session?.access_token && supabaseSchema) {
      setAuthContext({ token: session.access_token, schema: supabaseSchema });
    } else if (event === "SIGNED_OUT") {
      clearAuthContext();
    }
  });
  schemaLoaded = true;
  // Ask RDS for its cutover flags now, while the page runs its own first
  // reads, so the first overlaid read does not wait for them (skipped
  // during view-as). Deferred require, as for the overlays: rdsPhi imports
  // this module.
  if (accessToken && !isImp) {
    setTimeout(() => {
      try {
        // eslint-disable-next-line global-require
        require("../utils/rdsPhi").prefetchCutoverFlags();
      } catch (_) {
        // Only a head start: the first overlaid read fetches the flags anyway.
      }
    }, 0);
  }
  console.log(
    "[supabaseClient] loadSupabaseSchema() completed, schemaLoaded =",
    schemaLoaded,
  );
}

/**
 * Classify a Supabase/fetch error and log a developer-readable explanation.
 *
 * Background: When Supabase's Cloudflare edge returns HTTP 521 (origin server
 * down / overloaded), the response has no CORS headers, so browsers block it
 * and JavaScript only sees "TypeError: Failed to fetch" — the 521 status code
 * is never exposed. This function detects that pattern and logs the real
 * probable cause.
 *
 * @param {any}    error    The error thrown or returned by Supabase / fetch.
 * @param {string} context  Short label shown at the start of the log line
 *                          (e.g. "fetchPatientCount").
 * @returns {"network_failure"|"auth_error"|"rate_limit"|"server_error"|"unknown"}
 */
export function explainSupabaseError(error, context = "") {
  const prefix = context ? `[${context}] ` : "";
  const msg = String(error?.message ?? error ?? "").toLowerCase();
  const code = String(error?.code ?? "");
  const status = error?.status ?? error?.statusCode ?? null;

  // ── Network-level failures ───────────────────────────────────────────────
  // "Failed to fetch" is what browsers report when:
  //   • The server returns a non-2xx response with no CORS headers (e.g. 521)
  //   • The device is offline
  //   • A firewall / proxy resets the connection
  if (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("err_failed") ||
    msg.includes("err_connection_refused") ||
    msg.includes("load failed") || // Safari offline
    msg.includes("network request failed") ||
    (code === "" && msg.includes("typeerror"))
  ) {
    console.warn(
      `${prefix}Network error — could not reach Supabase. Likely causes:\n` +
        `  1. Supabase project overloaded or down — Cloudflare returns HTTP 521 (origin unreachable).\n` +
        `     When that happens the response carries no CORS headers, so browsers\n` +
        `     re-report the failure as a "CORS policy" error instead of 521.\n` +
        `  2. Device is offline or behind a restrictive network/firewall.\n` +
        `  3. Supabase free-tier project has been paused.\n` +
        `  → Check https://status.supabase.com and your project dashboard.\n` +
        `  Original error:`,
      error,
    );
    return "network_failure";
  }

  // ── Auth / JWT errors ────────────────────────────────────────────────────
  if (
    status === 401 ||
    status === 403 ||
    code === "PGRST301" ||
    msg.includes("jwt") ||
    msg.includes("invalid token") ||
    msg.includes("not authenticated")
  ) {
    console.warn(
      `${prefix}Auth error — the request was rejected by Supabase RLS or the JWT is invalid/expired.\n` +
        `  Original error:`,
      error,
    );
    return "auth_error";
  }

  // ── Rate limiting ────────────────────────────────────────────────────────
  if (status === 429 || code === "429" || msg.includes("rate limit")) {
    console.warn(
      `${prefix}Rate limited by Supabase — too many requests in a short window.\n` +
        `  Original error:`,
      error,
    );
    return "rate_limit";
  }

  // ── Server errors (5xx other than 521) ───────────────────────────────────
  if (status >= 500 && status < 600) {
    console.warn(
      `${prefix}Supabase server error (HTTP ${status}).\n` +
        `  Original error:`,
      error,
    );
    return "server_error";
  }

  return "unknown";
}


// ---------- View-as write blocker ----------

const READ_ONLY_RPC_ALLOWLIST = new Set([
  // Analytics — read-only by definition.
  "analytics_user_stats_bundle",
  "analytics_user_role",
  "analytics_user_schema",
  "analytics_tenant_schemas",
  "analytics_top_users",
  "analytics_user_pages",
  "analytics_user_series",
  // Schema inspection — read-only.
  "list_tenant_schemas",
]);

class ImpersonationWriteError extends Error {
  constructor(detail) {
    super(`view-as is read-only: ${detail}`);
    this.name = "ImpersonationWriteError";
  }
}

export function _wrapForViewAsBlock(client) {
  // eslint-disable-next-line global-require
  const { isImpersonating } = require("../utils/impersonation");
  return new Proxy(client, {
    get(target, prop) {
      // Cross-schema rows are written through client.schema(rowSchema) (see
      // utils/rowSchema.js). supabase-js implements that as `this.rest.schema()`
      // and hands back a bare PostgrestClient, so without this passthrough the
      // scoped client walks straight out of the view-as block. Re-wrap it.
      if (prop === "schema") {
        return (name) => _wrapForViewAsBlock(target.schema(name));
      }
      if (prop === "from") {
        return (table) => {
          const builder = target.from(table);
          if (!isImpersonating()) return builder;
          return new Proxy(builder, {
            get(b, k) {
              if (k === "insert" || k === "update" || k === "delete" || k === "upsert") {
                return () => {
                  throw new ImpersonationWriteError(`${k} on '${table}' blocked`);
                };
              }
              return Reflect.get(b, k);
            },
          });
        };
      }
      if (prop === "rpc") {
        return (name, args) => {
          if (!isImpersonating()) return target.rpc(name, args);
          if (READ_ONLY_RPC_ALLOWLIST.has(name)) return target.rpc(name, args);
          throw new ImpersonationWriteError(`rpc('${name}') not in read-only allowlist`);
        };
      }
      if (prop === "storage") {
        const s = target.storage;
        // A schema-scoped PostgrestClient has no storage — nothing to guard.
        if (!s || !isImpersonating()) return s;
        return new Proxy(s, {
          get(_s, k) {
            if (k === "from") {
              return (bucket) => {
                const sb = s.from(bucket);
                return new Proxy(sb, {
                  get(_b, kk) {
                    if (
                      kk === "upload" ||
                      kk === "update" ||
                      kk === "uploadToSignedUrl" ||
                      kk === "remove" ||
                      kk === "move" ||
                      kk === "copy"
                    ) {
                      // Marked so the storage overlay calls this thrower
                      // instead of writing RDS (see markBlockedStorageWrite).
                      return markBlockedStorageWrite(() => {
                        throw new ImpersonationWriteError(`storage.${kk} on '${bucket}' blocked`);
                      });
                    }
                    return Reflect.get(_b, kk);
                  },
                });
              };
            }
            return Reflect.get(s, k);
          },
        });
      }
      return Reflect.get(target, prop);
    },
  });
}
